/**
 * The account filter — the opportunity list's and analytics' (session 6).
 *
 * **On a parent it includes the sites**: the API reads `accountId` as the
 * account and every live account under it. The help text says so, from the one
 * shared wording. `accountScope=exact` — set by the dashboard's per-account
 * drill-through, whose row counts one account — narrows it, and the text then
 * says that instead. Choosing another account clears it.
 */

import { X } from 'lucide-react';
import { ACCOUNT_FILTER_EXACT_TEXT, ACCOUNT_FILTER_HELP_TEXT } from '@menlo/shared';
import { Button } from '@/components/ui/button';
import { AccountPicker } from '@/features/pickers/AccountPicker';
import { useApiQuery } from '@/lib/queries';

export function AccountFilter({
  value,
  scope,
  onChange,
  id,
}: {
  value: string | undefined;
  scope: string | undefined;
  /** The new account, or null to clear; the caller also clears `accountScope`. */
  onChange: (value: string | null) => void;
  id?: string;
}): React.ReactElement {
  // The name of an account that arrived in the URL, before any search finds it.
  const selected = useApiQuery<{ account: { id: string; name: string } }>(
    ['account', value ?? ''],
    value === undefined ? null : `/api/accounts/${value}`,
  );
  const current =
    value !== undefined && selected.data !== undefined
      ? { id: value, name: String(selected.data.account.name) }
      : null;
  const help = scope === 'exact' ? ACCOUNT_FILTER_EXACT_TEXT : ACCOUNT_FILTER_HELP_TEXT;

  return (
    <div className="min-w-[14rem] space-y-0.5" title={help}>
      <div className="flex items-center gap-1">
        <div className="flex-1">
          <AccountPicker
            {...(id === undefined ? {} : { id })}
            value={value ?? null}
            onChange={(next) => onChange(next)}
            allowCreate={false}
            current={current}
            placeholder="All accounts"
          />
        </div>
        {value !== undefined && (
          <Button
            variant="ghost"
            size="sm"
            className="h-8 px-2"
            aria-label="Clear the account filter"
            onClick={() => onChange(null)}
          >
            <X className="h-3.5 w-3.5" aria-hidden="true" />
          </Button>
        )}
      </div>
      <p className="text-[11px] text-muted-foreground">{help}</p>
    </div>
  );
}
